import { AnswerType, EntityType, StorageMappings, StoragePath } from "@types-runway/questionnaire";
import { safeParse } from "@types-runway/state";
import { Gender, GenderType } from "@types-runway/user";

export const buildEntityUpdate = (
  mappings: StoragePath<EntityType>[],
  answer: AnswerType
): Record<string, unknown> => {
  const updateObject: Record<string, unknown> = {};
  const isAnswerObject =
    typeof answer === "object" && answer !== null && !Array.isArray(answer);

  for (const mapping of mappings) {
    if ("propertyKey" in mapping) {
      const key = mapping.propertyKey as string;
      if (isAnswerObject && key in answer) {
        updateObject[key] = answer[key as keyof typeof answer];
      } else if (!isAnswerObject) {
        updateObject[key] = answer;
        return updateObject;
      }
    } else if ("nestingProperty" in mapping) {
      const key = mapping.nestingProperty as string;
      const subAnswer =
        isAnswerObject && key in answer
          ? answer[key as keyof typeof answer]
          : answer;
      const nestedObject = buildEntityUpdate(mapping.mappings, subAnswer);
      if (Object.keys(nestedObject).length > 0) {
        updateObject[key] = nestedObject;
      }
    }
  }

  return updateObject;
};

export interface EntityUpdateResult {
  entityType: EntityType;
  update: Record<string, unknown>;
}

export const resolveEntityUpdate = (
  storageMappings: StorageMappings<EntityType>,
  answer: AnswerType,
  existingNestedState?: Record<string, unknown>
): EntityUpdateResult | null => {
  const { entityType, mappings, nestingProperty } = storageMappings;

  const answerIsJsonString = typeof answer === "string" && safeParse(answer);

  let effectiveAnswer: AnswerType = answerIsJsonString
    ? JSON.parse(answer as string)
    : (Gender[answer as GenderType] ?? answer);

  // Unwrap nestingProperty if the answer is already wrapped
  if (
    nestingProperty &&
    typeof effectiveAnswer === "object" &&
    effectiveAnswer !== null &&
    !Array.isArray(effectiveAnswer)
  ) {
    const keys = Object.keys(effectiveAnswer);
    if (keys.length === 1 && keys[0] === nestingProperty) {
      effectiveAnswer = effectiveAnswer[nestingProperty as keyof typeof effectiveAnswer];
    }
  }

  const entityUpdate = buildEntityUpdate(mappings, effectiveAnswer);

  if (Object.keys(entityUpdate).length === 0) {
    return null;
  }

  if (entityType === "user") {
    const update = nestingProperty
      ? { [nestingProperty as string]: entityUpdate }
      : entityUpdate;
    return { entityType: "user", update };
  }

  if (entityType === "case") {
    if (nestingProperty) {
      const prevState = existingNestedState ?? {};
      const update = {
        [nestingProperty as string]: {
          ...prevState,
          ...entityUpdate,
        },
      };
      return { entityType: "case", update };
    }
    return { entityType: "case", update: entityUpdate };
  }

  return null;
};
